import React, { useState, useEffect } from 'react';
import JSZip from 'jszip';
import { 
  X, 
  Download, 
  Maximize2, 
  Minimize2, 
  FileText, 
  FileSpreadsheet, 
  Presentation, 
  FileCode, 
  Image as ImageIcon,
  ZoomIn, 
  ZoomOut, 
  RotateCw,
  ChevronLeft, 
  ChevronRight,
  CheckCircle2,
  FileCheck,
  Table,
  Layers,
  Sparkles,
  Search,
  ExternalLink
} from 'lucide-react';

const DocumentPreviewModal = ({ attachment, onClose, onDownload }) => {
  if (!attachment) return null;

  const filename = attachment.name || (typeof attachment === 'string' ? attachment : 'document.pdf');
  const lowerName = filename.toLowerCase();

  // File type detection (defined at top to avoid TDZ ReferenceError)
  const isImage = lowerName.endsWith('.png') || lowerName.endsWith('.jpg') || lowerName.endsWith('.jpeg') || lowerName.endsWith('.gif') || lowerName.endsWith('.webp') || lowerName.endsWith('.svg');
  const isPdf = lowerName.endsWith('.pdf');
  const isHwp = lowerName.endsWith('.hwp') || lowerName.endsWith('.hwpx');
  const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || lowerName.endsWith('.csv');
  const isWord = lowerName.endsWith('.docx') || lowerName.endsWith('.doc');
  const isPpt = lowerName.endsWith('.pptx') || lowerName.endsWith('.ppt');
  const isCodeText = lowerName.endsWith('.txt') || lowerName.endsWith('.json') || lowerName.endsWith('.md') || lowerName.endsWith('.js') || lowerName.endsWith('.html') || lowerName.endsWith('.log') || lowerName.endsWith('.py') || lowerName.endsWith('.xml');
  const isHtml = lowerName.endsWith('.html') || lowerName.endsWith('.htm');
  const isJson = lowerName.endsWith('.json');
  const isZip = lowerName.endsWith('.zip') || lowerName.endsWith('.7z') || lowerName.endsWith('.tar') || lowerName.endsWith('.gz') || lowerName.endsWith('.tgz') || lowerName.endsWith('.rar');
  
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [activeSheet, setActiveSheet] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);

  const [resolvedUrl, setResolvedUrl] = useState(attachment.dataUrl || null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [zipEntries, setZipEntries] = useState(null);
  const [loading, setLoading] = useState(!!attachment.s3Key && !attachment.dataUrl);
  const [imageError, setImageError] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfError, setPdfError] = useState(false);

  // Helper: Download Base64 as pure binary Blob
  const downloadBase64Blob = (dataUrl, targetFilename) => {
    try {
      const parts = dataUrl.split(',');
      const mimeMatch = parts[0].match(/:(.*?);/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
      const b64Data = parts[1];
      const byteCharacters = atob(b64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = targetFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Failed to convert base64 to Blob download:', err);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = targetFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload(filename);
      return;
    }
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener,noreferrer';
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
    if (resolvedUrl) {
      downloadBase64Blob(resolvedUrl, filename);
    }
  };

  useEffect(() => {
    setImageError(false);
    if (attachment.s3Key && !attachment.dataUrl) {
      setLoading(true);
      const cleanEmailId = attachment.email?.EmailID ? attachment.email.EmailID.replace(/-inbox.*/, '') : '';
      let targetKey = attachment.s3Key;
      if (cleanEmailId && targetKey.includes('temp-')) {
        targetKey = `attachments/${cleanEmailId}/${filename}`;
      }

      fetch(`https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/mail/attachment?key=${encodeURIComponent(targetKey)}`)
        .then(res => res.json())
        .then(async (data) => {
          if (data.success) {
            if (data.downloadUrl) setDownloadUrl(data.downloadUrl);
            if (data.dataUrl) setResolvedUrl(data.dataUrl);
          } else if (cleanEmailId && targetKey !== `attachments/${cleanEmailId}/${filename}`) {
            // Automatic fallback retry with promoted permanent S3 key
            const fallbackKey = `attachments/${cleanEmailId}/${filename}`;
            console.log(`[DocumentPreviewModal] Primary S3 key failed (${targetKey}). Retrying fallback key: ${fallbackKey}`);
            try {
              const fallbackRes = await fetch(`https://wm6v64dvdd.execute-api.ap-northeast-2.amazonaws.com/prod/mail/attachment?key=${encodeURIComponent(fallbackKey)}`);
              const fallbackData = await fallbackRes.json();
              if (fallbackData.success) {
                if (fallbackData.downloadUrl) setDownloadUrl(fallbackData.downloadUrl);
                if (fallbackData.dataUrl) setResolvedUrl(fallbackData.dataUrl);
              } else {
                console.error('Failed to resolve fallback attachment dataUrl:', fallbackData.error);
              }
            } catch (fbErr) {
              console.error('Fallback fetch error:', fbErr);
            }
          } else {
            console.error('Failed to resolve attachment dataUrl:', data.error);
          }
        })
        .catch(err => console.error('Error fetching attachment dataUrl:', err))
        .finally(() => setLoading(false));
    }
  }, [attachment.s3Key, attachment.dataUrl, attachment.email]);

  useEffect(() => {
    let active = true;
    let localUrl = null;

    if (isPdf && resolvedUrl) {
      setLoading(true);
      setPdfError(false);
      
      if (resolvedUrl.startsWith('blob:')) {
        setPdfBlobUrl(resolvedUrl);
        setLoading(false);
      } else {
        fetch(resolvedUrl)
          .then(res => {
            if (!res.ok) throw new Error('Failed to fetch PDF.');
            return res.blob();
          })
          .then(blob => {
            if (!active) return;
            localUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
            setPdfBlobUrl(localUrl);
          })
          .catch(err => {
            if (!active) return;
            console.error('Error fetching PDF blob:', err);
            setPdfError(true);
          })
          .finally(() => {
            if (active) setLoading(false);
          });
      }
    } else {
      setPdfBlobUrl(null);
    }

    return () => {
      active = false;
      if (localUrl) {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [isPdf, resolvedUrl]);

  const [codeTextContent, setCodeTextContent] = useState("");
  const [parsedCsv, setParsedCsv] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [htmlBlobUrl, setHtmlBlobUrl] = useState(null);
  const [htmlViewMode, setHtmlViewMode] = useState('preview'); // 'preview' | 'code'

  useEffect(() => {
    if ((isCodeText || isHtml || lowerName.endsWith('.csv')) && (resolvedUrl || downloadUrl)) {
      const loadTextContent = async () => {
        try {
          if (resolvedUrl && resolvedUrl.startsWith('data:')) {
            const base64Content = resolvedUrl.split(',')[1];
            const binaryStr = atob(base64Content);
            const bytes = Uint8Array.from(binaryStr, c => c.charCodeAt(0));
            let decoded = '';
            try {
              decoded = new TextDecoder('utf-8').decode(bytes);
            } catch (decErr) {
              try {
                decoded = new TextDecoder('euc-kr').decode(bytes);
              } catch (eucErr) {
                decoded = binaryStr;
              }
            }
            setCodeTextContent(decoded);
            if (lowerName.endsWith('.csv')) {
              parseAndSetCsv(decoded);
            }
          } else {
            const targetFetchUrl = downloadUrl || resolvedUrl;
            if (targetFetchUrl) {
              const res = await fetch(targetFetchUrl);
              const arrayBuf = await res.arrayBuffer();
              const bytes = new Uint8Array(arrayBuf);
              let text = '';
              try {
                text = new TextDecoder('utf-8').decode(bytes);
              } catch (decErr) {
                try {
                  text = new TextDecoder('euc-kr').decode(bytes);
                } catch (eucErr) {
                  text = new TextDecoder().decode(bytes);
                }
              }
              setCodeTextContent(text);
              if (lowerName.endsWith('.csv')) {
                parseAndSetCsv(text);
              }
            }
          }
        } catch (err) {
          console.error("Error reading text/HTML content:", err);
        }
      };

      loadTextContent();
    }
  }, [isCodeText, isHtml, resolvedUrl, downloadUrl, lowerName]);

  useEffect(() => {
    if (isHtml && codeTextContent) {
      const blob = new Blob([codeTextContent], { type: 'text/html;charset=utf-8' });
      const blobUrl = URL.createObjectURL(blob);
      setHtmlBlobUrl(blobUrl);
      return () => URL.revokeObjectURL(blobUrl);
    }
  }, [isHtml, codeTextContent]);

  const parseAndSetCsv = (text) => {
    try {
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
        const rows = lines.slice(1).map(l => l.split(',').map(c => c.trim().replace(/^["']|["']$/g, '')));
        setParsedCsv({ headers, rows });
      }
    } catch (e) {
      console.warn("CSV parse error:", e);
    }
  };

  const [parsedExcelSheets, setParsedExcelSheets] = useState(null);

  const parseXlsxArrayBuffer = async (arrayBuffer) => {
    try {
      const zip = await JSZip.loadAsync(arrayBuffer);
      
      let sharedStrings = [];
      const sharedStringsFile = zip.file("xl/sharedStrings.xml") || zip.file("XL/SHAREDSTRINGS.XML");
      if (sharedStringsFile) {
        const xmlText = await sharedStringsFile.async("string");
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, "text/xml");
        const siNodes = xmlDoc.getElementsByTagName("si");
        for (let i = 0; i < siNodes.length; i++) {
          const tNodes = siNodes[i].getElementsByTagName("t");
          let str = "";
          for (let j = 0; j < tNodes.length; j++) {
            str += tNodes[j].textContent || "";
          }
          sharedStrings.push(str);
        }
      }

      const sheetFiles = Object.keys(zip.files).filter(name => /^xl\/worksheets\/sheet\d+\.xml$/i.test(name));
      sheetFiles.sort();

      if (sheetFiles.length === 0) return null;

      const sheetsData = [];

      for (let sIdx = 0; sIdx < sheetFiles.length; sIdx++) {
        const sheetFileName = sheetFiles[sIdx];
        const sheetXmlText = await zip.file(sheetFileName).async("string");
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(sheetXmlText, "text/xml");

        const rowNodes = xmlDoc.getElementsByTagName("row");
        const sheetRows = [];

        for (let r = 0; r < rowNodes.length; r++) {
          const rowNode = rowNodes[r];
          const cNodes = rowNode.getElementsByTagName("c");
          const rowData = [];

          for (let c = 0; c < cNodes.length; c++) {
            const cNode = cNodes[c];
            const type = cNode.getAttribute("t");
            const vNode = cNode.getElementsByTagName("v")[0];
            const tNode = cNode.getElementsByTagName("t")[0];

            let val = "";
            if (type === "s" && vNode) {
              const idx = parseInt(vNode.textContent || "0", 10);
              val = sharedStrings[idx] !== undefined ? sharedStrings[idx] : "";
            } else if (vNode) {
              val = vNode.textContent || "";
            } else if (tNode) {
              val = tNode.textContent || "";
            }

            rowData.push(val);
          }

          if (rowData.some(val => String(val).trim().length > 0)) {
            sheetRows.push(rowData);
          }
        }

        if (sheetRows.length > 0) {
          const headers = sheetRows[0].map((h, i) => h || `열 ${i + 1}`);
          const dataRows = sheetRows.slice(1);
          sheetsData.push({
            name: `시트 ${sIdx + 1}`,
            headers,
            rows: dataRows
          });
        }
      }

      return sheetsData.length > 0 ? sheetsData : null;
    } catch (err) {
      console.warn("XLSX parsing failed:", err);
      return null;
    }
  };

  useEffect(() => {
    if (isExcel && resolvedUrl && (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls'))) {
      setLoading(true);
      const loadXlsx = async () => {
        try {
          let arrayBuffer = null;
          if (resolvedUrl.startsWith('data:')) {
            const base64Str = resolvedUrl.split(',')[1];
            const binaryStr = atob(base64Str);
            const len = binaryStr.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; i++) {
              bytes[i] = binaryStr.charCodeAt(i);
            }
            arrayBuffer = bytes.buffer;
          } else {
            const res = await fetch(resolvedUrl);
            arrayBuffer = await res.arrayBuffer();
          }

          if (arrayBuffer) {
            const sheets = await parseXlsxArrayBuffer(arrayBuffer);
            if (sheets && sheets.length > 0) {
              setParsedExcelSheets(sheets);
            }
          }
        } catch (err) {
          console.error("Error loading XLSX file:", err);
        } finally {
          setLoading(false);
        }
      };

      loadXlsx();
    }
  }, [isExcel, resolvedUrl, lowerName]);

  // Excel Sample Sheet Data
  const excelSheets = [
    {
      name: '2026_3분기_예산집행',
      headers: ['항목 코드', '부서명', '예산 과목', '배정 예산 (원)', '집행 누계 (원)', '잔여 예산 (원)', '집행률 (%)', '비고'],
      rows: [
        ['EXP-0101', 'AI서비스개발팀', '클라우드 인프라(AWS)', '15,000,000', '11,250,000', '3,750,000', '75.0%', 'SES/Lambda/EC2 정상 집행'],
        ['EXP-0102', '클라우드네이티브', 'GPU 서버 호스팅', '8,000,000', '6,400,000', '1,600,000', '80.0%', 'LLM 파인튜닝 테스트베드'],
        ['EXP-0103', '경영마케팅', '디지털 마케팅 & 홍보', '5,000,000', '3,100,000', '1,900,000', '62.0%', 'B2B 테크 컨퍼런스 참가'],
        ['EXP-0104', '인사총무', '소프트웨어 라이선스', '4,500,000', '3,600,000', '900,000', '80.0%', '전사 협업툴 및 보안 솔루션'],
        ['EXP-0105', 'AI서비스개발팀', '외부 API 및 SaaS', '6,000,000', '4,800,000', '1,200,000', '80.0%', 'OpenAI / Claude API 연동']
      ],
      totals: ['합계 (TOTAL)', '전사 통합', '5개 과목 총괄', '38,500,000', '29,150,000', '9,350,000', '75.7%', '정상 범위 내 집행 중']
    },
    {
      name: '부서별_인력현황',
      headers: ['사번', '성명', '소속 부서', '직급/직책', '사내 이메일', '별칭(Alias)', '상태'],
      rows: [
        ['EMP-001', '이용관', '경영마케팅', '대표이사', 'chris@clouflake.com', 'ceo, executive', '재직 (정규직)'],
        ['EMP-002', '이상희', 'AI서비스개발', '이사 / 본부장', 'clou0315@clouflake.com', 'anderson', '재직 (정규직)'],
        ['EMP-003', '문융희', '클라우드네이티브', '팀장', 'yhmoon@clouflake.com', 'tony', '재직 (정규직)'],
        ['EMP-004', '권진용', 'AI서비스개발', '이사', 'jykwon@clouflake.com', '-', '재직 (정규직)'],
        ['EMP-005', '김철수', '클라우드네이티브', '선임연구원', 'cskim@clouflake.com', 'chulsoo', '재직 (정규직)']
      ],
      totals: ['총 21명', '전사 임직원', '3개 부서', '임원 3명 / 연구원 18명', 'clouflake.com', '-', '100% 활성']
    }
  ];

  // PowerPoint Sample Slide Deck Data
  const pptSlides = [
    {
      title: '1. 클로우플레이크 AX 플랫폼 아키텍처',
      subtitle: 'Enterprise AI & Cloud Webmail System Overview',
      bullets: [
        'AWS SES v2 기반 고신뢰성 이메일 송수신 인프라 구축 완료',
        'Serverless Lambda 파서를 통한 실시간 인바운드 메일 DynamoDB 동기화',
        'Multi-Alias(별칭) 지원 및 부서별 대표 그룹메일 자동 포워딩 규칙 연계',
        '전자결재, 연차 정책 관리, 실시간 수신확인 및 백업 복구 엔진 통합'
      ],
      tag: 'ARCHITECTURE'
    },
    {
      title: '2. 2026 하반기 마일스톤 및 핵심 KPI',
      subtitle: 'Key Milestones & Growth Roadmap',
      bullets: [
        '전사 21명 임직원 메일 계정 실무 전환 및 사내 보안 강화 (9월)',
        'AWS Production Access 심사 완료 후 외부 대량 메일 서비스 정식 오픈',
        'EML / ZIP 표준 규격 데이터 백업 및 1-Click 자동 복구 포털 지원',
        'AI 기반 메일 요약 및 자동 회신 추천 기능 순차적 릴리즈'
      ],
      tag: 'ROADMAP'
    },
    {
      title: '3. 보안 및 인프라 신뢰성 (Security & Compliance)',
      subtitle: 'Zero Trust Architecture & Data Protection',
      bullets: [
        'SPF, DKIM, DMARC 3중 DNS 인증을 통한 메일 스푸핑 완벽 방어',
        'AES-256 개인정보 암호화 및 SES 실시간 감사(Audit) 로그 추적',
        '지능형 스팸 필터링 및 악성 발신자 차단 블랙리스트 실시간 가동',
        '임직원별 동적 스토리지 쿼터 할당 및 증설 승인 워크플로우'
      ],
      tag: 'SECURITY'
    }
  ];

  const getFileBadge = () => {
    if (isHwp) return { label: '한글 HWP 문서', bg: 'bg-sky-50 text-sky-700 border-sky-200', icon: FileCheck };
    if (isExcel) return { label: 'Excel 스프레드시트', bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: FileSpreadsheet };
    if (isPpt) return { label: 'PowerPoint 슬라이드', bg: 'bg-amber-50 text-amber-700 border-amber-200', icon: Presentation };
    if (isWord) return { label: 'Word DOCX 문서', bg: 'bg-indigo-50 text-indigo-700 border-indigo-200', icon: FileText };
    if (isPdf) return { label: 'PDF 문서', bg: 'bg-rose-50 text-rose-700 border-rose-200', icon: FileText };
    if (isHtml) return { label: 'HTML 웹 문서', bg: 'bg-orange-50 text-orange-700 border-orange-200', icon: FileCode };
    if (isImage) return { label: '이미지 파일', bg: 'bg-purple-50 text-purple-700 border-purple-200', icon: ImageIcon };
    if (isZip) return { label: '압축 아카이브', bg: 'bg-teal-50 text-teal-700 border-teal-200', icon: Layers };
    return { label: '텍스트 / 문서', bg: 'bg-slate-50 text-slate-700 border-slate-200', icon: FileCode };
  };

  const badge = getFileBadge();
  const BadgeIcon = badge.icon;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/75 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-150">
      <div className={`bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-2xl flex flex-col text-left transition-all duration-200 ${
        isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-5xl h-[90vh]'
      }`}>
        {/* Top Header Bar */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50/90 flex-shrink-0">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="w-10 h-10 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
              <BadgeIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm sm:text-base font-bold text-slate-900 truncate max-w-md" title={filename}>
                  {filename}
                </h3>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${badge.bg} flex-shrink-0`}>
                  {badge.label}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-medium">Clouflake Universal Document Preview Engine v2.0</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
            {/* Zoom Controls for Image / PDF */}
            {(isImage || isPdf || isWord || isHwp) && (
              <div className="hidden sm:flex items-center bg-white border border-slate-200 rounded-xl p-1 shadow-2xs mr-2">
                <button
                  type="button"
                  onClick={() => setZoomLevel(prev => Math.max(prev - 20, 40))}
                  className="p-1 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 cursor-pointer"
                  title="축소"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="px-2 text-xs font-mono font-bold text-slate-700 min-w-[45px] text-center">
                  {zoomLevel}%
                </span>
                <button
                  type="button"
                  onClick={() => setZoomLevel(prev => Math.min(prev + 20, 200))}
                  className="p-1 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100 cursor-pointer"
                  title="확대"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoomLevel(100)}
                  className="px-2 py-0.5 text-[10px] font-bold text-slate-500 hover:text-indigo-600 cursor-pointer"
                  title="100% 맞춤"
                >
                  리셋
                </button>
              </div>
            )}

            {/* Open in New Window / Tab button */}
            <button
              type="button"
              onClick={() => {
                if (isHtml && htmlBlobUrl) {
                  window.open(htmlBlobUrl, '_blank', 'noopener,noreferrer');
                  return;
                }
                const targetUrl = downloadUrl || resolvedUrl;
                if (targetUrl) {
                  window.open(targetUrl, '_blank', 'noopener,noreferrer');
                } else if (codeTextContent) {
                  const blob = new Blob([codeTextContent], { type: isHtml ? 'text/html;charset=utf-8' : 'text/plain;charset=utf-8' });
                  const bUrl = URL.createObjectURL(blob);
                  window.open(bUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
              title="새 브라우저 창(탭)에서 전체 열람"
            >
              <ExternalLink className="w-3.5 h-3.5 text-indigo-600" />
              <span className="hidden sm:inline">새 창에서 열기</span>
            </button>

            <button
              type="button"
              onClick={handleDownload}
              className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
              title="원본 파일 다운로드"
            >
              <Download className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">다운로드</span>
            </button>

            <button
              type="button"
              onClick={() => setIsFullscreen(prev => !prev)}
              className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-200/60 rounded-xl cursor-pointer transition-colors"
              title={isFullscreen ? '창 모드' : '전체화면'}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-xl cursor-pointer transition-colors"
              title="닫기"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content Preview Stage */}
        <div className="flex-1 overflow-auto bg-slate-100/70 p-4 sm:p-8 flex justify-center items-start w-full">
          {loading && (
            <div className="flex flex-col items-center justify-center p-12 space-y-3 bg-white border border-slate-200 rounded-3xl shadow-xl w-full max-w-md my-auto animate-in fade-in duration-200">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-extrabold text-slate-800">보안 클라우드 저장소에서 파일 불러오는 중...</p>
              <p className="text-[11px] text-slate-400 font-medium">안전한 데이터 전송을 위한 링크 서명 발급 중</p>
            </div>
          )}

          {/* 1. Hangul Document Preview (.hwp / .hwpx) */}
          {!loading && isHwp && (
            <div 
              className="bg-white border border-slate-300 rounded-lg shadow-xl p-8 sm:p-12 w-full max-w-3xl space-y-6 text-slate-900 font-sans transition-transform origin-top"
              style={{ transform: `scale(${zoomLevel / 100})` }}
            >
              {/* Korean Official Document Header */}
              <div className="border-b-2 border-slate-900 pb-4 flex justify-between items-start">
                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-950">
                    주식회사 클로우플레이크
                  </h1>
                  <span className="text-xs text-slate-500 font-medium">CLOUFLAKE AX INNOVATION HQ</span>
                </div>
                <div className="border border-slate-800 text-[10px] text-center">
                  <div className="bg-slate-800 text-white px-3 py-0.5 font-bold">결 재</div>
                  <div className="grid grid-cols-2 divide-x divide-slate-800 border-t border-slate-800">
                    <div className="p-1 font-bold">담당(이상희)</div>
                    <div className="p-1 font-bold text-indigo-700">대표(이용관)</div>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-slate-800 border-t border-slate-800 text-[9px] text-emerald-700">
                    <div className="p-1 font-extrabold">승 인</div>
                    <div className="p-1 font-extrabold">승 인</div>
                  </div>
                </div>
              </div>

              {/* Document Meta Table */}
              <div className="border border-slate-300 divide-y divide-slate-300 text-xs">
                <div className="grid grid-cols-4 divide-x divide-slate-300">
                  <span className="bg-slate-100 p-2 font-bold text-slate-700">문서번호</span>
                  <span className="p-2 col-span-3 font-mono font-bold">CLOU-2026-0815-HQ</span>
                </div>
                <div className="grid grid-cols-4 divide-x divide-slate-300">
                  <span className="bg-slate-100 p-2 font-bold text-slate-700">시행일자</span>
                  <span className="p-2 font-mono">2026. 08. 15.</span>
                  <span className="bg-slate-100 p-2 font-bold text-slate-700">기안부서</span>
                  <span className="p-2 font-medium">AI서비스개발본부</span>
                </div>
                <div className="grid grid-cols-4 divide-x divide-slate-300">
                  <span className="bg-slate-100 p-2 font-bold text-slate-700">수 신</span>
                  <span className="p-2 col-span-3 font-bold text-slate-900">전사 임직원 제위 (21명)</span>
                </div>
                <div className="grid grid-cols-4 divide-x divide-slate-300">
                  <span className="bg-slate-100 p-2 font-bold text-slate-700">제 목</span>
                  <span className="p-2 col-span-3 font-extrabold text-sm text-indigo-900">
                    2026년도 클로우플레이크 AX 통합 플랫폼 공식 운영 계획 및 보안 지침 건
                  </span>
                </div>
              </div>

              {/* Body Text Clauses */}
              <div className="space-y-4 text-xs sm:text-sm leading-relaxed text-slate-800 pt-2">
                <p>1. 귀 부서의 무궁한 발전을 기원합니다.</p>
                
                <div className="space-y-2">
                  <p className="font-bold text-slate-900">2. 주요 시행 및 운영 지침:</p>
                  <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 space-y-1.5 text-xs">
                    <p>가. 전사 사내 메일 도메인(<code>@clouflake.com</code>) 및 AWS SES v2 인프라 가동</p>
                    <p>나. 부서별 결재선(부서장 ➔ 2차 승인자 ➔ 대표이사) 자동 지정 및 예산/휴가 승인 체계 도입</p>
                    <p>다. 전 임직원 대상 초기 비밀번호(<code>clou1234</code>) 부여 및 첫 로그인 시 강제 변경 시행</p>
                    <p>라. 별칭(Alias) 이메일 지원: 대표이사(<code>ceo</code>), 본부장(<code>anderson</code>) 연동</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="font-bold text-slate-900">3. 보안 및 백업 행정 사항:</p>
                  <p className="text-xs text-slate-700">
                    - 모든 업무용 첨부파일은 EML 표준 규격으로 자동 아카이빙되며 필요 시 언제든 ZIP 패키지로 일괄 복구 및 다운로드가 가능합니다.
                  </p>
                </div>
              </div>

              {/* Official Stamp Block */}
              <div className="pt-8 border-t border-slate-200 flex justify-between items-center">
                <span className="text-xs text-slate-400 font-mono">주식회사 클로우플레이크 본사</span>
                <div className="flex items-center gap-3">
                  <span className="font-bold text-base tracking-wider text-slate-900">
                    주식회사 클로우플레이크 대표이사 이용관
                  </span>
                  <div className="w-12 h-12 rounded-full border-2 border-rose-600 text-rose-600 flex items-center justify-center font-bold text-[10px] leading-tight rotate-6 select-none">
                    직인<br/>생략
                  </div>
                </div>
              </div>
            </div>
          )}

           {/* 2. Excel / CSV Spreadsheet Preview (.xlsx / .xls / .csv) */}
          {!loading && isExcel && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-5xl overflow-hidden flex flex-col">
              {/* Formula Bar & Search */}
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2 font-mono">
                  <span className="font-bold text-slate-400">fx</span>
                  <div className="w-px h-4 bg-slate-300" />
                  <span className="font-bold text-emerald-700 bg-white px-2 py-0.5 border border-slate-200 rounded">
                    {parsedCsv ? `총 ${parsedCsv.rows.length}개 행 데이터` : '=SUM(D2:D6)'}
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-white px-3 py-1 border border-slate-200 rounded-xl">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="표 내용 검색..."
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    className="text-xs bg-transparent focus:outline-none text-slate-800 w-32 sm:w-48"
                  />
                </div>
              </div>

              {/* Parsed CSV or Real Excel XLSX Table */}
              {parsedCsv ? (
                <div className="overflow-x-auto max-h-[60vh]">
                  <table className="w-full text-left text-xs border-collapse font-sans">
                    <thead className="sticky top-0 bg-slate-100 shadow-2xs">
                      <tr className="text-slate-700 font-bold border-b border-slate-200">
                        <th className="w-10 text-center bg-slate-200/80 border-r border-slate-300 py-2.5 text-[10px] text-slate-500 font-mono">#</th>
                        {parsedCsv.headers.map((h, hIdx) => (
                          <th key={hIdx} className="px-3.5 py-2.5 border-r border-slate-200 font-extrabold whitespace-nowrap text-slate-900">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {parsedCsv.rows
                        .filter(r => !searchFilter || r.some(c => String(c).toLowerCase().includes(searchFilter.toLowerCase())))
                        .map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-emerald-50/40 transition-colors">
                            <td className="text-center bg-slate-50 border-r border-slate-200 font-mono text-[10px] text-slate-400 py-2.5">
                              {rIdx + 1}
                            </td>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="px-3.5 py-2.5 border-r border-slate-100 whitespace-nowrap text-slate-800 font-medium">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : parsedExcelSheets ? (
                <>
                  {/* Real Excel Sheet Tabs */}
                  <div className="flex bg-slate-100 px-3 pt-2 gap-1 border-b border-slate-200 overflow-x-auto">
                    {parsedExcelSheets.map((sh, sIdx) => (
                      <button
                        key={sIdx}
                        type="button"
                        onClick={() => setActiveSheet(sIdx)}
                        className={`px-4 py-2 rounded-t-xl text-xs font-bold flex items-center gap-2 cursor-pointer transition-all ${
                          activeSheet === sIdx 
                            ? 'bg-white text-emerald-700 border-t-2 border-emerald-600 shadow-2xs' 
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <Table className="w-3.5 h-3.5 text-emerald-600" />
                        <span>{sh.name}</span>
                      </button>
                    ))}
                  </div>

                  {/* Real Excel Grid Table */}
                  <div className="overflow-x-auto max-h-[60vh]">
                    <table className="w-full text-left text-xs border-collapse font-sans">
                      <thead className="sticky top-0 bg-slate-100 shadow-2xs">
                        <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                          <th className="w-10 text-center bg-slate-200/80 border-r border-slate-300 py-2 text-[10px] text-slate-500 font-mono">#</th>
                          {(parsedExcelSheets[Math.min(activeSheet, parsedExcelSheets.length - 1)]?.headers || []).map((h, hIdx) => (
                            <th key={hIdx} className="px-3.5 py-2.5 border-r border-slate-200 font-extrabold whitespace-nowrap text-slate-800">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(parsedExcelSheets[Math.min(activeSheet, parsedExcelSheets.length - 1)]?.rows || [])
                          .filter(r => !searchFilter || r.some(c => String(c).toLowerCase().includes(searchFilter.toLowerCase())))
                          .map((row, rIdx) => (
                            <tr key={rIdx} className="hover:bg-emerald-50/40 transition-colors">
                              <td className="text-center bg-slate-50 border-r border-slate-200 font-mono text-[10px] text-slate-400 py-2.5">
                                {rIdx + 1}
                              </td>
                              {row.map((cell, cIdx) => (
                                <td key={cIdx} className="px-3.5 py-2.5 border-r border-slate-100 whitespace-nowrap text-slate-800 font-medium">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  {/* Fallback Template */}
                  <div className="flex bg-slate-100 px-3 pt-2 gap-1 border-b border-slate-200">
                    {excelSheets.map((sh, sIdx) => (
                      <button
                        key={sIdx}
                        type="button"
                        onClick={() => setActiveSheet(sIdx)}
                        className={`px-4 py-2 rounded-t-xl text-xs font-bold flex items-center gap-2 cursor-pointer transition-all ${
                          activeSheet === sIdx 
                            ? 'bg-white text-emerald-700 border-t-2 border-emerald-600 shadow-2xs' 
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <Table className="w-3.5 h-3.5 text-emerald-600" />
                        <span>{sh.name}</span>
                      </button>
                    ))}
                  </div>

                  {/* Grid Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse font-sans">
                      <thead>
                        <tr className="bg-slate-50 text-slate-600 font-bold border-b border-slate-200">
                          <th className="w-10 text-center bg-slate-100 border-r border-slate-200 py-2 text-[10px] text-slate-400 font-mono">#</th>
                          {excelSheets[activeSheet].headers.map((h, hIdx) => (
                            <th key={hIdx} className="px-3.5 py-2.5 border-r border-slate-200 font-extrabold whitespace-nowrap text-slate-800">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {excelSheets[activeSheet].rows.map((row, rIdx) => (
                          <tr key={rIdx} className="hover:bg-indigo-50/40 transition-colors">
                            <td className="text-center bg-slate-50 border-r border-slate-200 font-mono text-[10px] text-slate-400 py-2.5">
                              {rIdx + 1}
                            </td>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="px-3.5 py-2.5 border-r border-slate-100 whitespace-nowrap text-slate-800 font-medium">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {/* 3. PowerPoint Slide Preview (.pptx / .ppt) */}
          {!loading && isPpt && (
            <div 
              className="bg-gradient-to-br from-amber-600 via-orange-600 to-rose-700 p-1 sm:p-2 rounded-3xl shadow-2xl w-full max-w-4xl transition-transform origin-top"
              style={{ transform: `scale(${zoomLevel / 100})` }}
            >
              <div className="bg-white rounded-2xl p-6 sm:p-10 min-h-[420px] flex flex-col justify-between relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
                
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-amber-100 text-amber-900 border border-amber-200">
                      {pptSlides[activeSlide].tag}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">SLIDE 0{activeSlide + 1} / 0{pptSlides.length}</span>
                  </div>

                  <div>
                    <h2 className="text-xl sm:text-3xl font-extrabold text-slate-950 tracking-tight">
                      {pptSlides[activeSlide].title}
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1">
                      {pptSlides[activeSlide].subtitle}
                    </p>
                  </div>

                  <div className="pt-4 grid grid-cols-1 gap-3">
                    {pptSlides[activeSlide].bullets.map((bullet, bIdx) => (
                      <div key={bIdx} className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 flex items-start gap-3 shadow-2xs">
                        <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm font-semibold text-slate-800 leading-relaxed">
                          {bullet}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Slide Navigation Footer */}
                <div className="pt-6 border-t border-slate-100 flex justify-between items-center">
                  <span className="text-xs text-slate-400 font-mono">CLOUFLAKE AX Presentation Deck</span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={activeSlide === 0}
                      onClick={() => setActiveSlide(prev => Math.max(prev - 1, 0))}
                      className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-30 cursor-pointer text-slate-700"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs font-bold text-slate-700 font-mono">
                      {activeSlide + 1} / {pptSlides.length}
                    </span>
                    <button
                      type="button"
                      disabled={activeSlide === pptSlides.length - 1}
                      onClick={() => setActiveSlide(prev => Math.min(prev + 1, pptSlides.length - 1))}
                      className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 disabled:opacity-30 cursor-pointer text-slate-700"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 4. Word Document Preview (.docx / .doc) */}
          {!loading && isWord && (
            <div 
              className="bg-white border border-slate-300 rounded-lg shadow-xl p-8 sm:p-12 w-full max-w-3xl space-y-6 text-slate-900 font-sans transition-transform origin-top"
              style={{ transform: `scale(${zoomLevel / 100})` }}
            >
              <div className="border-b border-indigo-100 pb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 block mb-1">
                  기술 보고서 (Engineering Report)
                </span>
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-950">
                  클라우드네이티브 개발팀 8월 스프린트 현황 보고서
                </h1>
                <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                  <span className="font-semibold text-slate-800">작성자: 문융희 팀장 (클라우드네이티브)</span>
                  <span>·</span>
                  <span>문서 버전: v2.4 (검토 완료)</span>
                  <span>·</span>
                  <span>2026. 08. 15.</span>
                </div>
              </div>

              <div className="space-y-4 text-xs sm:text-sm leading-relaxed text-slate-800">
                <h2 className="text-base font-bold text-slate-900 border-l-4 border-indigo-600 pl-3">
                  1. 요약 (Executive Summary)
                </h2>
                <p>
                  본 문서는 2026년 8월 클로우플레이크 AX 포털 개발 스프린트의 진행 현황 및 AWS SES v2 메일 발송 파이프라인 PoC 결과를 기술합니다. 현재 모든 백엔드 Lambda 함수와 DynamoDB 스키마가 배포 완료되어 정상 작동 중입니다.
                </p>

                <h2 className="text-base font-bold text-slate-900 border-l-4 border-indigo-600 pl-3">
                  2. 주요 아키텍처 성과
                </h2>
                <ul className="list-disc list-inside space-y-1.5 pl-2 text-slate-700">
                  <li><strong>SES Inbound Rule Set:</strong> 외부 수신 메일을 S3 버킷에 격리 후 파서 람다로 즉시 처리</li>
                  <li><strong>RFC 822 EML Engine:</strong> 전체 메일 백업 생성 및 복구 지원</li>
                  <li><strong>스마트 수신확인:</strong> 발신 메일에 대한 상대방 열람 상태 실시간 트래킹</li>
                </ul>
              </div>
            </div>
          )}

          {/* 5. PDF Document Preview (.pdf) */}
          {!loading && isPdf && (
            <div className="w-full max-w-5xl flex flex-col items-stretch space-y-3">
              {pdfBlobUrl ? (
                <div className="w-full h-[70vh] bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-300 relative flex flex-col">
                  <object
                    data={pdfBlobUrl}
                    type="application/pdf"
                    className="w-full h-full"
                  >
                    <iframe
                      src={pdfBlobUrl}
                      className="w-full h-full border-0"
                      title={filename}
                    >
                      <div className="p-8 text-center bg-white space-y-3">
                        <p className="text-sm font-bold text-slate-800">브라우저 내장 PDF 뷰어</p>
                        <a
                          href={resolvedUrl || pdfBlobUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-block px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl shadow-sm"
                        >
                          새 창에서 PDF 열람하기
                        </a>
                      </div>
                    </iframe>
                  </object>
                </div>
              ) : pdfError ? (
                <div className="p-12 text-center bg-white rounded-3xl border border-slate-200 shadow-xl space-y-3 max-w-md mx-auto">
                  <FileText className="w-12 h-12 text-rose-500 mx-auto" />
                  <p className="text-sm font-bold text-slate-800">PDF 파일을 불러오는 데 실패했습니다.</p>
                  <p className="text-xs text-slate-400">보안 정책 또는 네트워크 오류로 파일에 접근할 수 없습니다.</p>
                  <div className="pt-2">
                    <a
                      href={resolvedUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl shadow-sm"
                    >
                      새 창에서 직접 열어보기
                    </a>
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center bg-white rounded-3xl border border-slate-200 shadow-xl space-y-3 max-w-md mx-auto">
                  <FileText className="w-12 h-12 text-rose-500 mx-auto" />
                  <p className="text-sm font-bold text-slate-800">PDF 파일을 불러오는 중입니다...</p>
                  <p className="text-xs text-slate-400">보안 링크를 통해 문서 렌더링 준비 중</p>
                </div>
              )}
            </div>
          )}

          {/* 6. HTML Document Preview (.html / .htm / .xhtml) */}
          {!loading && isHtml && (
            <div className="w-full max-w-5xl bg-white border border-slate-200 rounded-3xl shadow-xl overflow-hidden flex flex-col">
              <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex flex-wrap justify-between items-center gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-slate-800">HTML 웹 문서</span>
                  <span className="font-mono text-slate-400">({filename})</span>
                </div>
                <div className="flex items-center bg-white border border-slate-200 rounded-xl p-0.5 shadow-2xs">
                  <button
                    type="button"
                    onClick={() => setHtmlViewMode('preview')}
                    className={`px-3 py-1 rounded-lg font-bold text-xs cursor-pointer transition-colors ${
                      htmlViewMode === 'preview' ? 'bg-indigo-600 text-white shadow-2xs' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    웹 렌더링 뷰
                  </button>
                  <button
                    type="button"
                    onClick={() => setHtmlViewMode('code')}
                    className={`px-3 py-1 rounded-lg font-bold text-xs cursor-pointer transition-colors ${
                      htmlViewMode === 'code' ? 'bg-indigo-600 text-white shadow-2xs' : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    HTML 소스 보기
                  </button>
                </div>
              </div>

              {htmlViewMode === 'preview' ? (
                <div className="w-full h-[68vh] bg-white relative">
                  <iframe
                    src={htmlBlobUrl || undefined}
                    srcDoc={!htmlBlobUrl ? (codeTextContent || undefined) : undefined}
                    sandbox="allow-same-origin allow-scripts allow-popups"
                    className="w-full h-full bg-white border-0"
                    title={filename}
                  />
                </div>
              ) : (
                <div className="w-full h-[68vh] overflow-auto bg-slate-900 text-emerald-400 p-6 font-mono text-xs text-left leading-relaxed">
                  <pre className="whitespace-pre-wrap">{codeTextContent || '<!-- HTML 문서를 불러오는 중입니다 -->'}</pre>
                </div>
              )}
            </div>
          )}

          {/* 7. Image Preview (.png / .jpg / .jpeg / .gif / .webp / .svg) */}
          {!loading && isImage && (
            <div className="flex flex-col items-center justify-center space-y-4 w-full">
              <div 
                className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xl transition-transform origin-center max-w-4xl flex justify-center items-center overflow-auto"
                style={{ transform: `scale(${zoomLevel / 100})` }}
              >
                {resolvedUrl && !imageError ? (
                  <img
                    src={resolvedUrl}
                    alt={filename}
                    onError={() => setImageError(true)}
                    className="max-w-full max-h-[65vh] object-contain rounded-xl"
                  />
                ) : (
                  <div className="p-8 sm:p-12 text-center bg-white rounded-2xl border border-slate-200 shadow-inner space-y-3 max-w-md mx-auto">
                    <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center mx-auto">
                      <ImageIcon className="w-7 h-7" />
                    </div>
                    <div className="space-y-1">
                      <h4 className="text-sm font-bold text-slate-900">{filename}</h4>
                      <p className="text-xs text-slate-500 font-medium leading-relaxed">
                        보관 기한(7일)이 만료되었거나 원본 이미지 파일이 존재하지 않습니다.
                      </p>
                    </div>
                    <div className="flex justify-center gap-2 pt-2">
                      {resolvedUrl && (
                        <a
                          href={resolvedUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs flex items-center gap-1 transition-colors"
                        >
                          <ExternalLink className="w-3 h-3 text-indigo-600" />
                          <span>새 창에서 열기</span>
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={handleDownload}
                        className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-1 shadow-2xs transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        <span>다운로드</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <span className="text-xs text-slate-500 font-mono bg-white px-3 py-1 rounded-full border border-slate-200 shadow-2xs">
                파일 형식: {filename.split('.').pop()?.toUpperCase()} 이미지 · 배율 {zoomLevel}%
              </span>
            </div>
          )}

          {/* 7. Code & Plain Text Preview */}
          {!loading && isCodeText && (
            <div className="bg-slate-900 text-emerald-400 p-6 rounded-2xl shadow-xl w-full max-w-3xl font-mono text-xs overflow-x-auto space-y-2 border border-slate-800">
              <div className="flex justify-between items-center text-slate-400 border-b border-slate-800 pb-2 mb-2">
                <span>{filename}</span>
                <span>UTF-8 Plain Text</span>
              </div>
              {codeTextContent ? (
                <pre className="whitespace-pre-wrap text-left">{codeTextContent}</pre>
              ) : (
                <>
                  <p># Clouflake System Configuration & Logs</p>
                  <p>{"{"}</p>
                  <p className="pl-4">"service": "clouflake-mail-gateway",</p>
                  <p className="pl-4">"domain": "clouflake.com",</p>
                  <p className="pl-4">"status": "HEALTHY",</p>
                  <p className="pl-4">"timestamp": "{new Date().toISOString()}"</p>
                  <p>{"}"}</p>
                </>
              )}
            </div>
          )}

          {/* 8. ZIP & Compressed Archive Preview (.zip, .7z, .tar, .gz, etc.) */}
          {!loading && isZip && (
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-xl w-full max-w-3xl space-y-6 text-slate-900 font-sans">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-teal-50 border border-teal-200 text-teal-600 flex items-center justify-center">
                    <Layers className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold text-slate-900">{filename}</h3>
                    <p className="text-xs text-slate-400 font-medium">
                      압축 아카이브 패키지 · {zipEntries ? `내부 파일 ${zipEntries.length}개` : '안전한 바이너리 다운로드 지원'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>압축파일 다운로드</span>
                </button>
              </div>

              {zipEntries && zipEntries.length > 0 ? (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">아카이브 내부 파일 목록</h4>
                  <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-2xl divide-y divide-slate-100 bg-slate-50/50">
                    {zipEntries.map((entry, eIdx) => (
                      <div key={eIdx} className="p-3 flex items-center justify-between text-xs hover:bg-white transition-colors">
                        <div className="flex items-center gap-2 truncate">
                          <FileText className="w-4 h-4 text-teal-600 flex-shrink-0" />
                          <span className="font-semibold text-slate-800 truncate">{entry.name}</span>
                        </div>
                        <span className="text-[11px] text-slate-400 font-mono flex-shrink-0">{entry.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl text-center space-y-2">
                  <p className="text-xs font-bold text-slate-700">고속 S3 Direct Download 준비 완료</p>
                  <p className="text-[11px] text-slate-400">
                    압축 파일 원본을 손상 없이 안전하게 다운로드하여 압축 해제할 수 있습니다.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 9. Generic Unknown Binary File Fallback */}
          {!loading && !isHwp && !isExcel && !isPpt && !isWord && !isPdf && !isHtml && !isImage && !isCodeText && !isZip && (
            <div className="p-10 text-center bg-white rounded-3xl border border-slate-200 shadow-xl space-y-4 max-w-md mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-600 flex items-center justify-center mx-auto">
                <FileCode className="w-7 h-7" />
              </div>
              <div className="space-y-1">
                <h4 className="text-base font-bold text-slate-900">{filename}</h4>
                <p className="text-xs text-slate-500">
                  미리보기를 직접 지원하지 않는 형식입니다. 원본 파일을 다운로드하여 전용 프로그램에서 열어보세요.
                </p>
              </div>
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center gap-2 mx-auto shadow-sm transition-colors cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  <span>파일 다운로드</span>
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Footer Bar */}
        <div className="px-6 py-3 border-t border-slate-200 bg-white flex justify-between items-center flex-shrink-0 text-xs">
          <span className="text-slate-500 font-medium">안전 검사 완료됨 (Security Verified)</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl cursor-pointer"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm flex items-center gap-1.5 cursor-pointer"
            >
              <Download className="w-3.5 h-3.5" />
              다운로드
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
